<script lang="ts" setup>
import { VdForm, VdFormField } from "@voodoo.io/voodoo-ui";
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";

import useBackendClient from "@/client";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useRandomLoadingSentences from "@/hooks/random-loading-sentences";
import usePublicationStatus from "@/hooks/usePublicationStatus";
import useVersionDiff from "@/hooks/useVersionDiff";
import links from "@/router/links";

import VersionDiffSelector from "../versionHistory/VersionDiffSelector.vue";
import { ENTITY_CONFIG } from "../versionHistory/versionDiff.config";
import type { EntityChange } from "../versionHistory/versionDiff.types";
import rocket from "./assets/rocket.svg";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const publishDone = ref(false);
const publishError = ref("");
const form = reactive({
  name: "",
});

const selectedChanges = ref<EntityChange[]>([]);
const modifiedEntityIds = ref<Set<string>>(new Set());
const missingEntities = ref<Array<{ typeLabel: string; names: string[] }>>([]);
const showStaleWarning = ref(false);

// Fetch version diff for the WIP version
const { isLoading: diffLoading, sections, diffGeneratedAt, refetch } = useVersionDiff(props.gameId, "live", "wip");
const { flags } = useFeatureFlags(props.gameId);

// Partial publish via REST
const { client } = useBackendClient();
const { refetch: refetchPublicationStatus } = usePublicationStatus(props.gameId);
const saveLoading = ref(false);
const { start: startSentencesRoll, stop: stopSentencesRoll, sentence: waitSentence } = useRandomLoadingSentences();

const publishedVersionId = ref("");

const saveForm = async () => {
  saveLoading.value = true;
  startSentencesRoll();

  try {
    // selectedChanges have scoped entityIds like "class::abc123"
    // Group by scope type, strip prefix, and match back to original section changes
    const selectedByScope = new Map<string, Set<string>>();
    selectedChanges.value.forEach((c) => {
      const [scope, id] = c.entityId.split("::", 2);
      if (!selectedByScope.has(scope)) {
        selectedByScope.set(scope, new Set());
      }
      selectedByScope.get(scope)!.add(id);
    });

    const groupedChanges: Record<string, EntityChange[]> = {};
    for (const [type, change] of Object.entries(ENTITY_CONFIG)) {
      const key = change.selectedKey;
      const ids = selectedByScope.get(type);
      const section = ids && sections.value.find((s) => s.key === change.sectionKey);
      groupedChanges[key] = section ? section.changes.filter((c) => ids.has(c.entityId)) : [];
    }

    const { data } = await client.post(`/app/${props.gameId}/version/partial-publish`, {
      versionName: form.name,
      versionDiffGeneratedAt: diffGeneratedAt.value,
      // Multi-link: linked offers follow their own status lifecycle at publish (e.g. a manually
      // terminated offer reaches STOPPED) instead of mirroring their test's state
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
      ...groupedChanges,
    });

    if (data?.success) {
      publishedVersionId.value = data?.versionId ?? "";
    } else {
      publishError.value = data?.errorMessage ?? "Your publish has failed";
    }
    publishDone.value = true;
  } catch (err: unknown) {
    const responseData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;

    const reason = responseData?.reason as string;
    if (reason === "error.configuration.PartialPublication.EntitiesModifiedSinceDiff") {
      const ids = new Set<string>();
      if (Array.isArray(responseData!.details)) {
        (responseData!.details as Array<{ entityId: string; entityType: string }>).forEach((d) => {
          ids.add(`${d.entityType.toLowerCase()}::${d.entityId}`);
        });
      }
      modifiedEntityIds.value = ids;
      showStaleWarning.value = true;
      refetch();
    } else if (reason === "error.configuration.PartialPublication.StaleChanges") {
      // details is keyed by selectedKey (e.g. selectedClasses), each value is an array of { entityId, reason }
      const ids = new Set<string>();
      const missingByType = new Map<string, string[]>();
      const details = responseData!.details as Record<string, Array<{ entityId: string; reason: string }>> | undefined;
      if (details) {
        // Build a name lookup from current sections before refetch replaces them
        const nameMap = new Map<string, string>();
        sections.value.forEach((s) => s.changes.forEach((c) => nameMap.set(c.entityId, c.displayName)));

        const selectedKeyToType = new Map(Object.entries(ENTITY_CONFIG).map(([type, cfg]) => [cfg.selectedKey, type]));
        Object.entries(details).forEach(([selectedKey, entities]) => {
          const type = selectedKeyToType.get(selectedKey);
          if (type) {
            entities.forEach((e) => {
              ids.add(`${type}::${e.entityId}`);
              if (e.reason === "MISSING") {
                const { label } = ENTITY_CONFIG[type];
                if (!missingByType.has(label)) {
                  missingByType.set(label, []);
                }
                missingByType.get(label)!.push(nameMap.get(e.entityId) ?? e.entityId);
              }
            });
          }
        });
      }
      modifiedEntityIds.value = ids;
      missingEntities.value = Array.from(missingByType, ([typeLabel, names]) => ({ typeLabel, names }));
      showStaleWarning.value = true;
      refetch();
    } else if (reason === "error.configuration.Application.Locked") {
      publishError.value =
        "Another publication is already in progress for this app. Please wait for it to finish and try again.";
      publishDone.value = true;
    } else {
      publishError.value = (responseData?.errorMessage as string) ?? "Your publish has failed";
      publishDone.value = true;
    }
  } finally {
    saveLoading.value = false;
    stopSentencesRoll();
    refetchPublicationStatus();
  }
};

const resetForm = () => {
  publishDone.value = false;
  publishError.value = "";
  modifiedEntityIds.value = new Set();
  missingEntities.value = [];
  showStaleWarning.value = false;
  refetch();
};

const goToVersion = (versionId: string) => {
  router.push({
    name: links.games.home,
    params: { gameId: props.gameId, versionId },
  });
};

const goToWIPVersion = () => goToVersion("wip");
const goToLiveVersion = () => goToVersion(publishedVersionId.value);

const onClose = () => {
  if (!saveLoading.value) {
    router.back();
  }
};

const onSelectedChangesUpdate = (changes: EntityChange[]) => {
  selectedChanges.value = changes;
};
</script>

<template>
  <vd-dialog visible class="partial-publish-dialog" :close-on-click-outside="!saveLoading" @close="onClose">
    <img :src="rocket" class="rocket" />

    <div v-if="saveLoading" class="publish-wait">
      <p>Please wait while your config is published</p>
      <p v-html="waitSentence"></p>
    </div>

    <template v-if="publishDone && !saveLoading">
      <div v-if="publishError" class="publish-error">
        <p>Something happened !</p>
        <p class="error" v-html="publishError"></p>
        <div class="actions">
          <vd-button size="large" @click="onClose">Close</vd-button>
          <vd-button size="large" @click="resetForm">Try again</vd-button>
        </div>
      </div>
      <div v-else class="publish-success">
        <p>Your config was published with success.</p>
        <div class="actions">
          <vd-button size="large" @click="goToLiveVersion">
            <vd-custom-icon name="live-icon" />
            Go to live published config
          </vd-button>
          <vd-button size="large" @click="goToWIPVersion">
            <vd-custom-icon name="traffic-plot-icon" />
            Go to new wip config
          </vd-button>
        </div>
      </div>
    </template>

    <div v-show="!publishDone && !saveLoading" class="publish-form">
      <h3>Are you sure you want to publish this version?</h3>
      <p>Be sure that all settings are correct before going through this process...</p>

      <div v-if="showStaleWarning" class="stale-warning">
        <VdIcon store="fa" name="triangle-exclamation" class="stale-warning__icon" />
        <div>
          <span>
            Some entities have been modified while you were selecting the changes to publish. Please review the
            highlighted changes and try again.
          </span>
          <div v-if="missingEntities.length > 0" class="stale-warning__missing">
            <span>The following changes have been reverted and are no longer available:</span>
            <ul>
              <li v-for="group in missingEntities" :key="group.typeLabel">
                <strong>{{ group.typeLabel }}:</strong>
                {{ group.names.join(", ") }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-loading="diffLoading" class="diff-selector-wrapper">
        <VersionDiffSelector
          v-show="!diffLoading"
          :sections="sections"
          :modified-entity-ids="modifiedEntityIds"
          @update:selected-changes="onSelectedChangesUpdate"
        />
      </div>

      <VdForm
        :form="form"
        size="large"
        save-button-label="Publish"
        :disable-save-button="selectedChanges.length === 0"
        @submit="saveForm"
        @cancel="onClose"
      >
        <VdFormField
          v-model="form.name"
          prop="name"
          type="text"
          label="New version name"
          placeholder="Enter new version name here before publishing"
          clearable
          required
          @keydown.enter.prevent="(e: Event) => e.preventDefault()"
        />
      </VdForm>
    </div>
  </vd-dialog>
</template>

<style lang="scss">
.partial-publish-dialog {
  .el-dialog {
    width: 90%;
    max-width: 1280px;
    max-height: none;
    overflow-y: visible;

    .content {
      height: auto;
      max-height: none;
      overflow: visible;
    }
  }

  p {
    text-align: center;
    word-break: normal;
  }

  .error {
    color: $red-alert;
  }

  .rocket {
    width: 60px;
    margin: size("M") calc(50% - 30px);
  }

  .publish-form {
    text-align: center;

    h3 {
      margin-bottom: size("XS");
      color: var(--vd-base-primary-color);
      font-weight: bold;
    }

    p {
      margin-top: 0;
    }
  }

  .stale-warning {
    display: flex;
    align-items: center;
    gap: size("S");
    padding: size("S") size("M");
    margin-bottom: size("M");
    background-color: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 6px;
    color: #d97706;
    font-size: font("S");
    font-weight: 500;
    text-align: left;

    &__icon {
      color: #d97706;
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }

    &__missing {
      margin-top: size("XS");

      ul {
        margin: size("XS") 0 0;
        padding-left: size("M");
      }
    }
  }

  .diff-selector-wrapper {
    text-align: left;
    margin-bottom: size("L");
    min-height: 200px;
  }

  .actions {
    text-align: center;
  }
}
</style>
